import { Controller } from "@hotwired/stimulus";

/**
 * Event Request Schedule Controller
 *
 * Handles the schedule step of the event request wizard:
 * - Sync primary day values to subsequent days when "Same for all days" is checked
 * - Toggle visibility of weekend/holiday rows
 */
export default class extends Controller {
  static targets = [
    "primaryStartTime",
    "primaryEndTime",
    "primaryCapacity",
    "sameForAllCheckbox",
    "subsequentDaysBox",
    "dayRow",
    "startTime",
    "endTime",
    "capacity",
    "nonBookableWarning",
  ];

  connect() {
    // Initial state on page load if checkbox is checked
    if (this.hasSameForAllCheckboxTarget && this.sameForAllCheckboxTarget.checked) {
      this.syncFromPrimary();
      if (this.hasSubsequentDaysBoxTarget) {
        this.setBoxState(this.subsequentDaysBoxTarget, true);
      }
    }
  }

  /**
   * Sync values from primary day to all subsequent days.
   *
   * Only the standard schedule has a primary day and a box of the rest to copy
   * it onto, and GR-3534 put its time fields back to Primer SelectPanels
   * (GR-3442 had made both paths native inputs; the Advanced Schedule Builder
   * keeps those, and renders none of these targets). So reads go through the
   * hidden input the panel keeps in sync, and writes go through the panel's
   * checkItem API so the dynamic button label moves with the value.
   */
  syncFromPrimary() {
    if (!this.hasSameForAllCheckboxTarget || !this.sameForAllCheckboxTarget.checked) {
      return;
    }

    const startTime = this.panelValue(this.primaryStartTimeTarget);
    const endTime = this.panelValue(this.primaryEndTimeTarget);
    const capacity = this.primaryCapacityTarget.value;

    this.startTimeTargets.forEach((panel) => {
      this.setPanelValue(panel, startTime);
    });
    this.endTimeTargets.forEach((panel) => {
      this.setPanelValue(panel, endTime);
    });
    this.capacityTargets.forEach((input) => {
      input.value = capacity;
    });

    // checkItem re-enables the panel's hidden submit input as a side effect;
    // a partially re-enabled row would submit without its id/date fields and
    // fail validation. Sync only runs while "Same schedule for all days" is
    // checked, so the box must end up disabled again.
    if (this.hasSubsequentDaysBoxTarget) {
      this.setBoxState(this.subsequentDaysBoxTarget, true);
    }
  }

  panelValue(panel) {
    return panel.querySelector("[data-list-inputs] input")?.value;
  }

  // A panel with nothing picked posts nothing. Copying that would wipe every
  // other day's clock rather than leaving it where it was, so an empty primary
  // says nothing rather than saying none.
  setPanelValue(panel, value) {
    if (!value) return;

    // checkItem no-ops when the item is already selected.
    panel.checkItem?.(panel.getItemById?.(value));
  }

  // GR-3448: a day's attendance changed, and the guest room block follows it
  // for every night nobody has customized. The block is a sibling section, so
  // this announces on the document rather than reaching across the DOM; the
  // row's own hidden date field says which day changed.
  attendanceChanged(event) {
    const input = event.target;
    const row = input.closest("[data-event-request-schedule-target='dayRow']");
    const date = row?.querySelector("input[name$='[date]']")?.value;
    if (!date) return;

    document.dispatchEvent(
      new CustomEvent("event-request-schedule:attendance", {
        detail: { date, capacity: input.value },
        bubbles: true,
      })
    );
  }

  /**
   * Toggle between same/different schedules for all days
   */
  toggleSameForAll(event) {
    const sameForAll = event.target.checked;

    // Sync values if enabling "same for all"
    if (sameForAll) {
      this.syncFromPrimary();
    }

    if (this.hasSubsequentDaysBoxTarget) {
      this.setBoxState(this.subsequentDaysBoxTarget, sameForAll);
    }
  }

  /**
   * Set disabled state on a section box with proper accessibility attributes.
   * Inputs marked with `data-no-disable` are exempt: controls that change the
   * box's *display* rather than its content must stay interactive while the
   * box itself is disabled. The show-weekends-&-holidays toggle is the only
   * one, and it only renders where the range touches a weekend or a blocked
   * date.
   *
   * A day row goes whole or not at all. Its id and date are swept along with
   * its times, so a disabled row is simply absent from the payload, which is
   * what "Same schedule for all days" means: the server copies day one's
   * clock across in sync_same_schedule_to_all_days! and the rows do not need
   * to post. GR-2968's bug was the half of that, a row posting a time with no
   * id, which read as a brand new day.
   *
   * GR-3426 briefly exempted the Advanced Schedule Builder's per-day Title
   * along with its row's id and date. GR-3447 took the mark off all three and
   * took the builder out of this branch entirely: it renders no checkbox and
   * no box, so there is nothing here to sweep it.
   *
   * GR-3442 pointed the sweep at native time inputs; GR-3534 put the standard
   * step's panels back, so it reaches the hidden input a SelectPanel submits
   * through again. Same selector either way, and the builder renders no box
   * for it to run over at all.
   */
  setBoxState(box, disabled) {
    if (!box) return;
    box.style.opacity = disabled ? "0.6" : "1";
    box.setAttribute("aria-disabled", disabled ? "true" : "false");

    box
      .querySelectorAll(
        "input:not([data-no-disable]), select:not([data-no-disable]), textarea:not([data-no-disable]), button:not([data-no-disable])"
      )
      .forEach((el) => (el.disabled = disabled));
  }

  /**
   * Toggle visibility of non-bookable day rows (weekends/holidays)
   */
  toggleNonBookable(event) {
    const show = event.target.checked;

    // Show/hide non-bookable day rows
    this.dayRowTargets.forEach((row) => {
      if (row.dataset.nonBookable === "true") {
        row.classList.toggle("d-none", !show);
        row.setAttribute("aria-hidden", show ? "false" : "true");
      }
    });

    // Show/hide warning message
    if (this.hasNonBookableWarningTarget) {
      this.nonBookableWarningTarget.classList.toggle("d-none", !show);
    }
  }
}
